import { useEffect, useState } from 'react'
import { isIndependentUser, isProvider, type RoleProfile } from '@shared/rbac/member-roles'
import { useAuth } from '../contexts/auth-context'
import { loadMyProfile } from '../services/profile-service'
import { isSessionsNavVisibleFor } from '../config/nav-config'
import { followsAnySessions, useSessionFollowing, type SessionFollowingState } from '../stores/session-following-store'

/**
 * Freshest role profile: seeded synchronously from the auth user, refined by
 * the `me` profile. Errors keep the seed (never fail open).
 */
function useRoleProfile(): { profile: RoleProfile | null; key: string | null; settled: boolean } {
  const { user } = useAuth()
  const [refined, setRefined] = useState<{ key: string; profile: RoleProfile } | null>(null)
  const [failedFor, setFailedFor] = useState<string | null>(null)
  const userId = user?.userId ?? null
  // Signed-in users whose auth seed lacks an id still refine from `me`.
  const key = user ? (userId ?? 'me') : null

  useEffect(() => {
    if (!key) return
    let cancelled = false
    void loadMyProfile()
      .then((profile) => {
        if (!cancelled) setRefined({ key, profile })
      })
      .catch(() => {
        // Keep the auth seed.
        if (!cancelled) setFailedFor(key)
      })
    return () => {
      cancelled = true
    }
  }, [key])

  const current = refined && refined.key === key ? refined.profile : null
  return {
    profile: current ?? user ?? null,
    key,
    settled: current != null || (key != null && failedFor === key),
  }
}

/**
 * Role profile + the Independent follow summary (only fetched for
 * Independents), shared by the nav hook and the route guard.
 */
export function useSessionsAccessInputs(): {
  profile: RoleProfile | null
  /** The `me` profile fetch has resolved or failed. */
  settled: boolean
  following: SessionFollowingState
} {
  const { profile, key, settled } = useRoleProfile()
  const independent = profile != null && isIndependentUser(profile)
  const following = useSessionFollowing(independent ? key : null)
  return { profile, settled, following }
}

/**
 * Whether the Sessions tab shows in the primary nav (desktop rail + mobile
 * bottom bar) for the signed-in user: providers, members, and Independents
 * who follow at least one member's sessions — see `isSessionsNavVisibleFor`.
 *
 * Seeded synchronously from the auth user's `roleType` so eligible users see
 * their nav immediately and non-following Independents never see a Sessions
 * flash (followers appear once the server summary lands). Fails CLOSED.
 *
 * Mirrors the established `useIsProviderUser` / `useRoleSimplifiedNav` pattern.
 */
export function useSessionsNavVisible(): boolean {
  const { profile, following } = useSessionsAccessInputs()
  return isSessionsNavVisibleFor(profile, { followsSessions: followsAnySessions(following) })
}

/**
 * Whether the signed-in user sees the Sessions tab as a session FOLLOWER (an
 * Independent following at least one member — not providers/members, who see
 * it by role). `pending` while the role or the follow summary is still
 * loading; a summary error reads as `not_follower` (fails closed).
 */
export type SessionFollowerStatus = 'pending' | 'follower' | 'not_follower'

export function useSessionFollowerStatus(): SessionFollowerStatus {
  const { profile, settled, following } = useSessionsAccessInputs()
  if (!profile) return 'not_follower'
  if (profile.roleType == null && !settled) return 'pending'
  if (!isIndependentUser(profile)) return 'not_follower'
  if (following.status === 'idle' || following.status === 'loading') return 'pending'
  return followsAnySessions(following) ? 'follower' : 'not_follower'
}

/** True only for an Independent who sees the Sessions tab as a follower. */
export function useIsSessionFollower(): boolean {
  return useSessionFollowerStatus() === 'follower'
}

/** True for providers (who run sessions) — gates provider-only walkthrough steps. */
export function useIsSessionProvider(): boolean {
  const { user } = useAuth()
  return user != null && isProvider(user) && !isIndependentUser(user)
}

/** True for Independents (family, guardians, support coordinators) — picks their walkthrough steps. */
export function useIsIndependentUser(): boolean {
  const { user } = useAuth()
  return user != null && isIndependentUser(user)
}
